import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Status } from '@df/core/status';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ProductAbstract } from '../../catalog/ui/product/product.abstract';
import { WishlistRemoveComponent } from '../../catalog/ui/wishlist/remove/wishlist-remove.component';
import { BasketItemDecorator } from '../../customer/basket/basket-item.decorator';
import { MinibasketComponent } from '../minibasket/minibasket.component';

@Component({
    selector: 'basket-wishlist-item',
    templateUrl: './basket-wishlist-item.component.html',
    styleUrl: './basket-wishlist-item.component.scss',
    standalone: true,
    imports: [CommonModule, DfImageDirective, OptionLabelPipe, PriceComponent, RouterModule, WishlistRemoveComponent, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BasketWishlistItemComponent extends ProductAbstract implements OnInit {
    @Input()
    item!: BasketItemDecorator;

    status = new Status();

    protected minibasket = inject(MinibasketComponent);

    get wishlist() {
        return this.app.customer.wishlist;
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
    }

    /**
     * Adds item to basket
     */
    addToBasket(allItems: boolean = false): Promise<void> {
        let addToBasketData;

        if (allItems) {
            addToBasketData = this.wishlist.items
                .filter(item => item.product?.isSalable())
                .map(item => ({
                    id: item.product?.data?.id,
                    qty: 1
                }));
        } else {
            if (!this.isSalable) {
                return Promise.resolve();
            }

            addToBasketData = [
                {
                    id: this.item.product?.data?.id,
                    qty: 1
                }
            ];
        }

        if (addToBasketData.length === 0) {
            return Promise.resolve();
        }

        const addPromise = this.basketService.add(this.status, addToBasketData);

        if (addPromise && typeof addPromise.then === 'function') {
            return addPromise
                .then(() => {
                    if (allItems) {
                        const itemIds = this.wishlist.items.filter(item => item.product?.isSalable()).map(item => item.data.id);
                        this.removeFromWishlist(itemIds);
                    } else {
                        const itemId = this.item.data.id;
                        this.removeFromWishlist([itemId]);
                    }
                })
                .catch(error => {
                    console.error('Failed to add items to basket:', error);
                    this.detectChanges();
                });
        }

        return Promise.resolve();
    }

    removeFromWishlist(itemIds: number[]): void {
        const removePromise = this.wishlist.remove(this.status, itemIds, (productsIds: number[]) => {
            this.handleRemoveSuccess(productsIds);
        });

        if (removePromise && typeof removePromise.then === 'function') {
            removePromise
                .then(() => {
                    this.detectChanges();
                })
                .catch(error => {
                    console.error('Failed to remove items from wishlist:', error);
                    this.detectChanges();
                });
        }
    }

    handleRemoveSuccess(productsIds: number[]): void {
        this.detectChanges();
        this.minibasket.showAddToBasketNotification();
    }

    get isSalable(): boolean | undefined {
        return this.item.product?.isSalable();
    }

    get configurableOptions(): Array<{ code: string; value: any }> {
        if (this.item.product?.configurableAttributeCodes) {
            return this.item.product.configurableAttributeCodes.map(code => ({
                code,
                value: this.item.product.data[code]
            }));
        }
    }
}
